import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ArrowLeft, Mail01 } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { apiRequest } from '@/shared/api/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import { AUTH_FORM_SIZE } from '@/shared/config/auth-form';
import { ROUTES } from '@/shared/config/constants';
import { KnowMatchLogo, ParticleSphere, TextLink } from '@/shared/ui';

const forgotSchema = z.object({
  email: z.string().trim().min(1, 'Informe seu email').email('Email inválido'),
});

type ForgotFormValues = z.infer<typeof forgotSchema>;

export const ForgotPasswordPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<ForgotFormValues>({
    resolver: zodResolver(forgotSchema),
    mode: 'onBlur',
    defaultValues: { email: '' },
  });

  const onSubmit = async (values: ForgotFormValues) => {
    setSubmitting(true);
    try {
      await apiRequest<void>({
        method: 'POST',
        url: ENDPOINTS.AUTH.FORGOT_PASSWORD,
        data: { email: values.email },
      });
      setSent(true);
      toast.success('Se o e-mail estiver cadastrado, você vai receber o link em instantes.');
    } catch {
      // Mesmo comportamento em caso de erro (anti-enumeracao no backend).
      setSent(true);
      toast.success('Se o e-mail estiver cadastrado, você vai receber o link em instantes.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-dvh w-full flex-col bg-white lg:grid lg:grid-cols-2">
      {/* Mobile only: logo no topo, estilo header de app. */}
      <header className="flex items-center justify-center px-6 pt-[calc(env(safe-area-inset-top)+24px)] pb-2 lg:hidden">
        <KnowMatchLogo className="h-16 w-auto text-gray-900" aria-label="Know-How Match" />
      </header>

      <aside className="relative hidden min-h-dvh flex-col overflow-hidden bg-black text-white lg:flex">
        <div className="relative z-10 flex items-center gap-3 p-10">
          <KnowMatchLogo className="h-9 w-auto text-white" />
        </div>

        <div className="absolute inset-0 flex items-center justify-center">
          <ParticleSphere className="aspect-square h-full max-h-[120%] w-full max-w-[120%]" />
        </div>

        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black via-black/70 to-transparent"
          aria-hidden="true"
        />

        <div className="relative z-10 mt-auto flex flex-col gap-3 p-10">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight">
            Vamos te ajudar a entrar
          </h2>
          <p className="max-w-md text-base leading-relaxed text-white/60">
            Enviamos um link de recuperação para o e-mail cadastrado. Cheque sua caixa de entrada.
          </p>
        </div>
      </aside>

      <section className="flex flex-1 items-center justify-center px-6 py-6 sm:px-10 lg:min-h-dvh lg:py-16">
        <div className="w-full max-w-md">
          <TextLink to={ROUTES.LOGIN} className="mb-6">
            <ArrowLeft className="size-4" aria-hidden="true" />
            Voltar para o login
          </TextLink>

          <header className="flex flex-col gap-2">
            <h1 className="text-3xl font-semibold tracking-tight text-gray-900 md:text-4xl">
              {sent ? 'Link enviado' : 'Esqueceu a senha?'}
            </h1>
            <p className="text-base text-gray-500">
              {sent
                ? `Se ${getValues('email')} estiver cadastrado, você vai receber um email em alguns minutos.`
                : 'Informe o email da sua conta e enviamos um link de recuperação.'}
            </p>
          </header>

          {!sent && (
            <form
              className="mt-10 flex flex-col gap-5"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <Input
                    label="Email"
                    type="email"
                    size={AUTH_FORM_SIZE}
                    icon={Mail01}
                    placeholder="voce@empresa.com"
                    autoComplete="email"
                    autoFocus
                    isInvalid={!!errors.email}
                    hint={errors.email?.message}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                )}
              />

              <Button
                type="submit"
                size={AUTH_FORM_SIZE}
                color="primary"
                isLoading={submitting}
                className="w-full bg-black ring-black hover:bg-gray-900 data-loading:bg-gray-900"
              >
                Enviar link de recuperação
              </Button>
            </form>
          )}

          {sent && (
            <div className="mt-10 flex flex-col gap-4">
              <Button
                type="button"
                size={AUTH_FORM_SIZE}
                color="secondary"
                onClick={() => setSent(false)}
                className="w-full"
              >
                Usar outro email
              </Button>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};
